import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, CheckCircle, Mail, Clock } from 'lucide-react';
import { LocationSelector } from '@/components/LocationSelector';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function CompleteProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [formData, setFormData] = useState({
    parent_first_name: '',
    parent_last_name: '',
    parent_role: '',
    parent_role_other: '',
    email: '',
    phone: '',
    location_type: 'DC Ward',
    location_detail: '',
    ward_number: '',
    city_county: '',
    address: '',
    children: [{
      name: '',
      age: '',
      gender: 'boy',
      learning_stage: 'Early Learning',
      reading_level: 'Developing',
      favorite_interests: ''
    }],
    has_library_card: '',
    already_has_bookshelf: '',
    bookshelf_reason: '',
    books_from_birth_signup: '',
    sms_consent: '',
    books_count: 'None',
    annual_income: '',
    photo_video_consent: '',
    referral_source: '',
    referral_source_other: ''
  });

  const incomeOptions = [
    { value: '', label: 'Select income range' },
    { value: 'Under $15,000', label: 'Under $15,000' },
    { value: '$15,000 - $25,000', label: '$15,000 - $25,000' },
    { value: '$25,000 - $35,000', label: '$25,000 - $35,000' },
    { value: '$35,000 - $50,000', label: '$35,000 - $50,000' },
    { value: '$50,000 - $75,000', label: '$50,000 - $75,000' },
    { value: '$75,000 - $100,000', label: '$75,000 - $100,000' },
    { value: 'Over $100,000', label: 'Over $100,000' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  const referralOptions = [
    { value: '', label: 'Select an option' },
    { value: 'School', label: 'School' },
    { value: 'Community Event', label: 'Community Event' },
    { value: 'Friend', label: 'Friend' },
    { value: 'Online', label: 'Online' },
    { value: 'Other', label: 'Other' }
  ];

  const parentRoleOptions = [
    { value: '', label: 'Select role' },
    { value: 'Mother', label: 'Mother' },
    { value: 'Father', label: 'Father' },
    { value: 'Guardian', label: 'Guardian' },
    { value: 'Other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required Yes/No questions
    if (formData.has_library_card === '' || 
        formData.already_has_bookshelf === '' || 
        formData.books_from_birth_signup === '' ||
        formData.sms_consent === '' ||
        formData.photo_video_consent === '') {
      toast.error('Please answer all required questions in the Questions section');
      return;
    }

    // Validate bookshelf reason if they have a bookshelf
    if (formData.already_has_bookshelf === 'yes' && !formData.bookshelf_reason.trim()) {
      toast.error('Please explain why you would like another bookshelf');
      return;
    }

    setLoading(true);
    
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const submitData = {
        ...formData,
        user_id: user.id,
        // Convert Yes/No strings to booleans for backend
        has_library_card: formData.has_library_card === 'yes',
        already_has_bookshelf: formData.already_has_bookshelf === 'yes',
        books_from_birth_signup: formData.books_from_birth_signup === 'yes',
        sms_consent: formData.sms_consent === 'yes',
        photo_video_consent: formData.photo_video_consent === 'yes',
        children: formData.children.map(child => ({
          ...child,
          age: parseInt(child.age) || 0
        }))
      };
      
      await api.post('/families', submitData);
      
      // Update user in localStorage to mark profile as completed
      const updatedUser = { ...user, profile_completed: true };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile completed successfully!');
      setProfileCompleted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const addChild = () => {
    setFormData({
      ...formData,
      children: [...formData.children, {
        name: '',
        age: '',
        gender: 'boy',
        learning_stage: 'Early Learning',
        reading_level: 'Developing',
        favorite_interests: '',
        has_library_card: false
      }]
    });
  };

  const removeChild = (index) => {
    setFormData({
      ...formData,
      children: formData.children.filter((_, i) => i !== index)
    });
  };

  const updateChild = (index, field, value) => {
    const newChildren = [...formData.children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setFormData({ ...formData, children: newChildren });
  };

  // Yes/No Radio Component
  const YesNoQuestion = ({ id, label, value, onChange, required = true }) => (
    <div className="p-4 border border-stone-200 rounded-lg bg-stone-50">
      <Label className="block mb-3 font-medium">{label} {required && '*'}</Label>
      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={id}
            value="yes"
            checked={value === 'yes'}
            onChange={() => onChange('yes')}
            className="w-4 h-4 text-primary focus:ring-primary"
            required={required}
          />
          <span>Yes</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={id}
            value="no"
            checked={value === 'no'}
            onChange={() => onChange('no')}
            className="w-4 h-4 text-primary focus:ring-primary"
          />
          <span>No</span>
        </label>
      </div>
    </div>
  );

  // Success landing page after profile completion
  if (profileCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full border-stone-200 shadow-xl" data-testid="profile-complete-card">
          <CardContent className="pt-8 pb-8 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-green-800 mb-3" data-testid="success-title">
              Profile Complete! 🎉
            </h2>
            
            <p className="text-stone-600 mb-6">
              Thank you for completing your family profile with The Bookshelf Project!
            </p>
            
            {/* Next Steps Card */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border-2 border-primary/20 rounded-xl p-6 mb-6 text-left">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-bold text-primary text-lg">What Happens Next?</h3>
              </div>
              
              <p className="text-stone-700 mb-4 font-medium">
                You will receive your official Welcome Email shortly with next steps for scheduling your bookshelf delivery.
              </p>
              
              <div className="flex items-start gap-2 text-stone-600 text-sm">
                <Clock className="w-4 h-4 mt-0.5 text-amber-600" />
                <span>Please allow 1-2 business days for our team to review your profile and send your Welcome Email.</span>
              </div>
            </div>
            
            {/* What to Expect */}
            <div className="bg-stone-50 rounded-lg p-4 mb-6 text-left">
              <h4 className="font-semibold text-stone-800 mb-2">📬 Keep an eye on your inbox for:</h4>
              <ul className="text-sm text-stone-600 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  A confirmation email for completing your profile
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  Your official Welcome Email with scheduling instructions
                </li>
              </ul>
            </div>
            
            <Button
              onClick={() => navigate('/family-portal')}
              className="w-full rounded-full"
              data-testid="go-to-portal-button"
            >
              Go to Family Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card className="border-stone-200 shadow-floating mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">Complete Your Family Profile</CardTitle>
            <CardDescription>
              Please provide your family information to get started with The Bookshelf Project - Building In-Home Libraries for Children
            </CardDescription>
          </CardHeader>
        </Card>

        <form onSubmit={handleSubmit}>
          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Parent Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parent_first_name">First Name *</Label>
                  <Input
                    id="parent_first_name"
                    data-testid="parent-first-name"
                    value={formData.parent_first_name}
                    onChange={(e) => setFormData({ ...formData, parent_first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="parent_last_name">Last Name *</Label>
                  <Input
                    id="parent_last_name"
                    data-testid="parent-last-name"
                    value={formData.parent_last_name}
                    onChange={(e) => setFormData({ ...formData, parent_last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="parent_role">Parent/Guardian Role</Label>
                <select
                  id="parent_role"
                  data-testid="parent-role"
                  value={formData.parent_role}
                  onChange={(e) => setFormData({ ...formData, parent_role: e.target.value, parent_role_other: e.target.value !== 'Other' ? '' : formData.parent_role_other })}
                  className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  {parentRoleOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {formData.parent_role === 'Other' && (
                  <Input
                    type="text"
                    value={formData.parent_role_other}
                    onChange={(e) => setFormData({ ...formData, parent_role_other: e.target.value })}
                    placeholder="Please specify your role..."
                    className="mt-2 h-11"
                    data-testid="parent-role-other"
                    required
                  />
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    data-testid="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    data-testid="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Location Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <LocationSelector formData={formData} setFormData={setFormData} required={true} />
              
              <div>
                <Label htmlFor="address">Full Address *</Label>
                <Input
                  id="address"
                  data-testid="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address, city, state, zip"
                  required
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Children Information</CardTitle>
                <Button type="button" variant="outline" onClick={addChild} data-testid="add-child-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Child
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {formData.children.map((child, index) => (
                <div key={index} className="p-4 border border-stone-200 rounded-lg space-y-4" data-testid={`child-${index}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">Child {index + 1}</h4>
                    {formData.children.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeChild(index)}
                        data-testid={`remove-child-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        data-testid={`child-name-${index}`}
                        value={child.name}
                        onChange={(e) => updateChild(index, 'name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>Age *</Label>
                      <Input
                        data-testid={`child-age-${index}`}
                        type="number"
                        min="0"
                        max="18"
                        value={child.age}
                        onChange={(e) => updateChild(index, 'age', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>Gender *</Label>
                      <select
                        data-testid={`child-gender-${index}`}
                        value={child.gender}
                        onChange={(e) => updateChild(index, 'gender', e.target.value)}
                        className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="boy">Boy</option>
                        <option value="girl">Girl</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Learning Stage *</Label>
                      <select
                        data-testid={`child-learning-stage-${index}`}
                        value={child.learning_stage}
                        onChange={(e) => updateChild(index, 'learning_stage', e.target.value)}
                        className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="Early Learning">Early Learning</option>
                        <option value="Kindergarten">Kindergarten</option>
                        <option value="1st Grade">1st Grade</option>
                        <option value="2nd Grade">2nd Grade</option>
                        <option value="3rd Grade">3rd Grade</option>
                        <option value="Not in School">Not in School</option>
                        <option value="Homeschooling">Homeschooling</option>
                      </select>
                    </div>
                    <div>
                      <Label>Reading Level *</Label>
                      <select
                        data-testid={`child-reading-level-${index}`}
                        value={child.reading_level}
                        onChange={(e) => updateChild(index, 'reading_level', e.target.value)}
                        className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="Developing">Developing</option>
                        <option value="Grade Level">Grade Level</option>
                        <option value="Advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Favorite Books, TV Characters, Superheroes, or Interests</Label>
                    <Input
                      data-testid={`child-interests-${index}`}
                      value={child.favorite_interests}
                      onChange={(e) => updateChild(index, 'favorite_interests', e.target.value)}
                      placeholder="e.g., Harry Potter, Spider-Man, dinosaurs"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Household Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="books_count">Number of Children's Books in Home *</Label>
                  <select
                    id="books_count"
                    data-testid="books-count"
                    value={formData.books_count}
                    onChange={(e) => setFormData({ ...formData, books_count: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="None">None</option>
                    <option value="1-10">1-10</option>
                    <option value="11-20">11-20</option>
                    <option value="More than 20">More than 20</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="annual_income">Annual Household Income *</Label>
                  <select
                    id="annual_income"
                    data-testid="annual-income"
                    value={formData.annual_income}
                    onChange={(e) => setFormData({ ...formData, annual_income: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    required
                  >
                    {incomeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="referral_source">How did you learn about The Bookshelf Project? *</Label>
                <select
                  id="referral_source"
                  data-testid="referral-source"
                  value={formData.referral_source}
                  onChange={(e) => setFormData({ ...formData, referral_source: e.target.value, referral_source_other: e.target.value !== 'Other' ? '' : formData.referral_source_other })}
                  className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  required
                >
                  {referralOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {formData.referral_source === 'Other' && (
                  <Input
                    type="text"
                    value={formData.referral_source_other}
                    onChange={(e) => setFormData({ ...formData, referral_source_other: e.target.value })}
                    placeholder="Please specify how you heard about us..."
                    className="mt-2 h-11"
                    data-testid="referral-source-other"
                    required
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Questions</CardTitle>
              <CardDescription>Please answer all of the following questions (required)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <YesNoQuestion
                id="has_library_card"
                label="Does your child have a library card?"
                value={formData.has_library_card}
                onChange={(value) => setFormData({ ...formData, has_library_card: value })}
              />
              
              <YesNoQuestion
                id="already_has_bookshelf"
                label="Does your family already have a bookshelf?"
                value={formData.already_has_bookshelf}
                onChange={(value) => setFormData({ ...formData, already_has_bookshelf: value })}
              />
              
              {/* Conditional bookshelf reason field */}
              {formData.already_has_bookshelf === 'yes' && (
                <div className="ml-4 p-4 border-l-4 border-amber-400 bg-amber-50 rounded-r-lg">
                  <Label htmlFor="bookshelf_reason" className="block mb-2 font-medium text-amber-800">
                    Since you already have a bookshelf, please share why you would like another: *
                  </Label>
                  <Textarea
                    id="bookshelf_reason"
                    data-testid="bookshelf-reason"
                    value={formData.bookshelf_reason}
                    onChange={(e) => setFormData({ ...formData, bookshelf_reason: e.target.value })}
                    placeholder="Please explain why you would like another bookshelf..."
                    className="min-h-[100px]"
                    required={formData.already_has_bookshelf === 'yes'}
                  />
                </div>
              )}
              
              <YesNoQuestion
                id="books_from_birth_signup"
                label="Is your child signed up with Books from Birth (DC Public Library)?"
                value={formData.books_from_birth_signup}
                onChange={(value) => setFormData({ ...formData, books_from_birth_signup: value })}
              />
              
              <YesNoQuestion
                id="sms_consent"
                label="Do you consent to receiving text notifications from The Bookshelf Project?"
                value={formData.sms_consent}
                onChange={(value) => setFormData({ ...formData, sms_consent: value })}
              />
              
              <YesNoQuestion
                id="photo_video_consent"
                label="Do you consent to video and photos for promotional purposes?"
                value={formData.photo_video_consent}
                onChange={(value) => setFormData({ ...formData, photo_video_consent: value })}
              />
            </CardContent>
          </Card>

          <Button
            type="submit"
            className="w-full rounded-full h-12 text-lg"
            disabled={loading}
            data-testid="submit-button"
          >
            {loading ? 'Creating Profile...' : 'Complete Profile & Get Started'}
          </Button>
        </form>
      </div>
    </div>
  );
}
